/**
 * File-type icons for the Files browser.
 *
 * Inline SVG rather than an icon font so there is no new dependency and the
 * glyphs colour with `currentColor` like the rest of the app. Categories are
 * coarse on purpose — a file manager that drew a different glyph for every
 * media type would be noise, not signal.
 */

import type { ReactElement } from 'react';
import './FileIconComponent.css';

export type FileIconKind =
  | 'folder'
  | 'folder-open'
  | 'markdown'
  | 'text'
  | 'pdf'
  | 'image'
  | 'document'
  | 'spreadsheet'
  | 'slides'
  | 'code'
  | 'audio'
  | 'video'
  | 'archive'
  | 'generic';

/** Map a VFS entry's kind + mediaType to an icon category. */
export function fileIconKindFor(kind: 'file' | 'directory', mediaType: string, name: string): FileIconKind {
  if (kind === 'directory') return 'folder';
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  if (ext === 'md' || mediaType === 'text/markdown') return 'markdown';
  if (ext === 'pdf' || mediaType === 'application/pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || mediaType.startsWith('image/')) return 'image';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'document';
  if (['csv', 'xlsx', 'xls', 'ods'].includes(ext) || mediaType === 'text/csv' || mediaType.includes('spreadsheet')) return 'spreadsheet';
  if (['ppt', 'pptx', 'odp'].includes(ext) || mediaType.includes('presentation')) return 'slides';
  if (['js', 'ts', 'tsx', 'jsx', 'json', 'py', 'rb', 'go', 'rs', 'java', 'sh', 'yml', 'yaml', 'xml', 'html', 'css'].includes(ext)) return 'code';
  if (['mp3', 'wav', 'm4a', 'ogg', 'flac'].includes(ext) || mediaType.startsWith('audio/')) return 'audio';
  if (['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(ext) || mediaType.startsWith('video/')) return 'video';
  if (['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)) return 'archive';
  if (mediaType === 'text/plain' || mediaType.startsWith('text/')) return 'text';
  return 'generic';
}

const PATHS: Record<FileIconKind, string> = {
  // A closed folder. The open variant below is the same body with the front
  // flap dropped forward — the universal 'this folder is open' cue, rather than
  // the angled sliver the old path drew.
  folder: 'M2 6a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6Z',
  'folder-open': 'M3 7a1 1 0 0 1 1-1h5l2 2h6a1 1 0 0 1 1 1v1H6.5L4 14V8a1 1 0 0 0-1 1Zm1 4 2.2-2H20l-2.5 7.3a1 1 0 0 1-.95.7H3.9a1 1 0 0 1-.9-1.4Z',
  markdown: 'M3 5h18v14H3V5Zm3 9v-4l2.5 3L11 10v4M14 10v4M14 10l2.5 3L19 10v4',
  text: 'M5 3h9l5 5v13H5V3Zm9 0v5h5M8 12h8M8 15h8M8 18h5',
  pdf: 'M5 3h9l5 5v13H5V3Zm9 0v5h5M8 13h2v3M14 13h1.5v3H14v-1.5',
  image: 'M5 3h14v18H5V3Zm2 12 3-3 3 3 4-4 2 2v5H7v-3Z',
  document: 'M5 3h9l5 5v13H5V3Zm9 0v5h5M8 12h8M8 15h6',
  spreadsheet: 'M5 3h14v18H5V3Zm0 6h14M5 13h14M9 3v18M14 3v18',
  slides: 'M4 4h16v12H4V4Zm2 14h12M8 8h8v4H8V8Z',
  code: 'M4 4h16v16H4V4Zm5 5-3 3 3 3M15 9l3 3-3 3M13 7l-2 10',
  audio: 'M12 3v14m0-14a4 4 0 0 1 0 8m-3 6h6',
  video: 'M5 4h9l5 5v11H5V4Zm3 5 4 2.5L8 14V9Z',
  archive: 'M4 5h16v4H4V5Zm1 4h14v11H5V9Zm5 2v4M10 15h4',
  generic: 'M5 3h9l5 5v13H5V3Zm9 0v5h5',
};

export interface FileIconComponentProps {
  kind: FileIconKind;
  /** 16px by default; pass through to size to a toolbar or breadcrumb. */
  size?: number;
  className?: string;
}

export function FileIconComponent({ kind, size = 16, className }: FileIconComponentProps): ReactElement {
  return (
    <svg
      className={`file-icon file-icon-${kind} ${className ?? ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[kind]} />
    </svg>
  );
}
